import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Footer from '../components/Footer';
import Select from '../components/Select';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { usersService, settingsService, creditService, employeeDocService } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const addMonths = (dateStr, months) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
};

const ROLE_META = {
  employee:    { label: 'User',        color: '#64748B', bg: 'rgba(100,116,139,0.1)' },
  admin:       { label: 'Admin',       color: '#2563EB', bg: 'rgba(37,99,235,0.1)'   },
  super_admin: { label: 'Super Admin', color: '#B91C1C', bg: 'rgba(185,28,28,0.1)'   },
};

const INTERN_BADGE = { label: 'Intern', color: '#0891B2', bg: 'rgba(8,145,178,0.1)' };

const RoleBadge = ({ role, employmentType }) => {
  const m = ROLE_META[role] || ROLE_META.employee;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
      <span style={{
        display: 'inline-block', padding: '3px 8px', borderRadius: '2px',
        border: `1px solid ${m.color}33`, background: m.bg, color: m.color,
        fontWeight: '600', fontSize: 'var(--font-size-xs)', letterSpacing: '0.04em', whiteSpace: 'nowrap'
      }}>
        {m.label}
      </span>
      {employmentType === 'intern' && (
        <span style={{
          display: 'inline-block', padding: '3px 8px', borderRadius: '2px',
          border: `1px solid ${INTERN_BADGE.color}33`, background: INTERN_BADGE.bg, color: INTERN_BADGE.color,
          fontWeight: '600', fontSize: 'var(--font-size-xs)', letterSpacing: '0.04em', whiteSpace: 'nowrap'
        }}>
          {INTERN_BADGE.label}
        </span>
      )}
    </span>
  );
};

const RoleDropdown = ({ user, canAssignSuperAdmin, changing, onRoleChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const m = ROLE_META[user.role] || ROLE_META.employee;
  const isLocked = !canAssignSuperAdmin && user.role === 'super_admin';
  const roles = ['employee', 'admin', ...(canAssignSuperAdmin ? ['super_admin'] : [])];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        disabled={changing || isLocked}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '3px 7px', borderRadius: '2px',
          border: `1px solid ${m.color}44`, background: m.bg,
          color: m.color, fontWeight: '600',
          fontSize: 'var(--font-size-xs)', letterSpacing: '0.04em',
          cursor: isLocked || changing ? 'default' : 'pointer',
          whiteSpace: 'nowrap', outline: 'none', lineHeight: 1.4
        }}
      >
        {changing
          ? <div className="spinner spinner-sm" style={{ width: '9px', height: '9px', borderWidth: '2px', borderColor: `${m.color}44`, borderTopColor: m.color }} />
          : null}
        {m.label}
        {!changing && !isLocked && (
          <svg width="7" height="4" viewBox="0 0 7 4" style={{ opacity: 0.55, flexShrink: 0 }}>
            <path d="M0 0l3.5 4L7 0z" fill="currentColor" />
          </svg>
        )}
      </button>

      {open && (
        <div className="custom-select-list" style={{ minWidth: '130px' }}>
          {roles.map(role => {
            const rm = ROLE_META[role];
            const isCurrent = user.role === role;
            return (
              <button
                key={role}
                type="button"
                className={`custom-select-item${isCurrent ? ' current' : ''}`}
                onClick={() => { setOpen(false); if (!isCurrent) onRoleChange(role); }}
              >
                <span>{rm.label}</span>
                {isCurrent && (
                  <svg width="11" height="11" viewBox="0 0 12 12" style={{ flexShrink: 0, color: 'var(--color-primary)' }}>
                    <polyline points="1,6 4.5,9.5 11,2.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Overflow menu for a row's secondary actions. Rendered through a portal
// because the table's card has overflow:auto, which would otherwise clip an
// absolutely-positioned dropdown (same approach as the Leads badge picker).
const ActionsMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const visible = items.filter(Boolean);
  if (visible.length === 0) return null;

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        title="More actions"
        className="btn btn-secondary btn-sm"
        style={{ padding: '2px 9px', fontSize: '15px', lineHeight: 1.1, letterSpacing: '1px' }}
      >
        ···
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={menuRef} style={{ position: 'fixed', top: pos.top, right: pos.right, background: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.14)', zIndex: 10000, minWidth: '160px', overflow: 'hidden', padding: '4px' }}>
          {visible.map((item, i) => (
            <button
              key={i}
              type="button"
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '7px 12px', fontSize: '13px', border: 'none', borderRadius: '4px',
                background: 'transparent', color: item.danger ? '#DC2626' : 'var(--color-text)',
                cursor: item.disabled ? 'not-allowed' : 'pointer', opacity: item.disabled ? 0.4 : 1,
              }}
              onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = item.danger ? 'rgba(220,38,38,0.07)' : '#F3F4F6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const Users = () => {
  const { showNotification, currentUser, isSuperAdmin, isAdmin, realUser, startImpersonation } = useInvoice();
  const confirm = useConfirm();
  const [viewingUser, setViewingUser] = useState(null);
  const [resetModal, setResetModal] = useState(null); // { user } | { user, done: true, password }
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addData, setAddData] = useState({ firstName: '', lastName: '', shortName: '', employeeNo: '', password: '', role: 'employee', employmentType: 'permanent', internDuration: '3' });
  const [addLoading, setAddLoading] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    shortName: '',
    employeeNo: '',
    officePhone: '',
    personalPhone: '',
    officeEmail: '',
    personalEmail: '',
    address: '',
    position: '',
    joinDate: '',
    employmentType: 'permanent',
    internshipEndDate: ''
  });
  const [editSaveLoading, setEditSaveLoading] = useState(false);

  // Credit balance state (super_admin only)
  const [creditTxns, setCreditTxns] = useState([]);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditAdding, setCreditAdding] = useState(false);
  const [editUserBalance, setEditUserBalance] = useState(0);

  // Documents state
  const [empDocs, setEmpDocs] = useState([]);
  const [empDocsLoading, setEmpDocsLoading] = useState(false);

  // Role changing
  const [roleChanging, setRoleChanging] = useState({}); // { [userId]: true }

  // Archived view
  const [showArchived, setShowArchived] = useState(false);
  const [archivedUsers, setArchivedUsers] = useState([]);

  const photoInputRef = useRef(null);
  const aadharInputRef = useRef(null);
  const offerLetterInputRef = useRef(null);
  const certInputRef = useRef(null);

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getUsers();
      setUsers(data.users);
    } catch (error) {
      showNotification(error.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = async () => {
    setAddData({ firstName: '', lastName: '', shortName: '', employeeNo: '', password: '', role: 'employee', employmentType: 'permanent', internDuration: '3' });
    setShowAddPassword(false);
    setShowAddModal(true);
    try {
      const defaultPwd = await settingsService.get('default_employee_password');
      if (defaultPwd) {
        setAddData(prev => ({ ...prev, password: defaultPwd }));
      }
    } catch (_) {}
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    const loginId = addData.shortName.trim().toLowerCase().replace(/\s+/g, '');
    setAddLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const newUser = await usersService.createUser({
        firstName: addData.firstName.trim(),
        lastName: addData.lastName.trim() || undefined,
        loginId,
        password: addData.password,
        employeeNo: addData.employeeNo.trim() || undefined,
        role: addData.role,
        employmentType: addData.employmentType,
        internshipEndDate: addData.employmentType === 'intern' ? addMonths(today, parseInt(addData.internDuration)) : null
      });
      setUsers([newUser, ...users]);
      setShowAddModal(false);
      setAddData({ firstName: '', lastName: '', shortName: '', employeeNo: '', password: '', role: 'employee', employmentType: 'permanent', internDuration: '3' });
      showNotification(`Employee added. Login ID: ${loginId}`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to add employee', 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditClick = async (user) => {
    setEditingUserId(user.id);
    setEditData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      shortName: user.loginId || '',
      employeeNo: user.employeeNo || '',
      officePhone: user.officePhone || '',
      personalPhone: user.personalPhone || '',
      officeEmail: user.officeEmail || '',
      personalEmail: user.personalEmail || '',
      address: user.address || '',
      position: user.position || '',
      joinDate: user.joinDate ? user.joinDate.split('T')[0] : '',
      employmentType: user.employmentType || 'permanent',
      internshipEndDate: user.internshipEndDate ? user.internshipEndDate.split('T')[0] : ''
    });
    setCreditAmount('');
    setCreditNote('');
    setCreditTxns([]);
    setEditUserBalance(parseFloat(user.creditBalance || 0));
    setShowEditModal(true);

    if (isAdmin()) {
      try {
        const data = await creditService.getTransactions(user.id);
        setCreditTxns(data.transactions || []);
        setEditUserBalance(parseFloat(data.balance || 0));
      } catch (_) {}
    }

    setEmpDocsLoading(true);
    try {
      const data = await employeeDocService.getDocuments(user.id);
      setEmpDocs(data.documents || []);
    } catch (_) {}
    setEmpDocsLoading(false);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingUserId(null);
    setCreditTxns([]);
    setCreditAmount('');
    setCreditNote('');
    setEmpDocs([]);
  };

  useEffect(() => {
    const anyOpen = viewingUser || resetModal || showAddModal || showEditModal;
    if (!anyOpen) return;
    const fn = (e) => {
      if (e.key !== 'Escape') return;
      if (resetModal) { setResetModal(null); return; }
      if (viewingUser) { setViewingUser(null); return; }
      if (showAddModal) { setShowAddModal(false); return; }
      if (showEditModal) { handleCloseEdit(); return; }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingUser, resetModal, showAddModal, showEditModal]);

  const handleDocUpload = async (type, files) => {
    if (!files || files.length === 0) return;
    try {
      const data = await employeeDocService.uploadDocument(editingUserId, type, Array.from(files));
      setEmpDocs(prev =>
        type === 'certificate'
          ? [...prev, ...data.documents]
          : [...prev.filter(d => d.type !== type), ...data.documents]
      );
      showNotification('Document uploaded', 'success');
    } catch (error) {
      showNotification(error.message || 'Upload failed', 'error');
    }
  };

  const handleDocDelete = async (docId) => {
    try {
      await employeeDocService.deleteDocument(editingUserId, docId);
      setEmpDocs(prev => prev.filter(d => d.id !== docId));
      showNotification('Document deleted', 'success');
    } catch (error) {
      showNotification(error.message || 'Delete failed', 'error');
    }
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      showNotification('Amount must be greater than 0', 'error');
      return;
    }
    setCreditAdding(true);
    try {
      const result = await creditService.addCredit(editingUserId, {
        amount: parseFloat(creditAmount),
        note: creditNote.trim() || undefined
      });
      setEditUserBalance(parseFloat(result.newBalance));
      setCreditTxns(prev => [result.transaction, ...prev]);
      setUsers(users.map(u => u.id === editingUserId
        ? { ...u, creditBalance: result.newBalance }
        : u
      ));
      setCreditAmount('');
      setCreditNote('');
      showNotification(`₹${parseFloat(creditAmount).toFixed(2)} credit added`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to add credit', 'error');
    } finally {
      setCreditAdding(false);
    }
  };

  const handleSaveEdit = async (e) => {
    if (e) e.preventDefault();
    if (!editData.employeeNo) {
      showNotification('Employee number is required', 'error');
      return;
    }
    const loginId = editData.shortName.trim().toLowerCase().replace(/\s+/g, '');
    if (!loginId) {
      showNotification('Preferred Short Name is required', 'error');
      return;
    }
    setEditSaveLoading(true);
    try {
      const updatedUser = await usersService.updateProfile(editingUserId, {
        ...editData,
        loginId
      });
      setUsers(users.map(u => u.id === editingUserId ? updatedUser : u));
      handleCloseEdit();
      showNotification('Employee updated successfully', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to update employee', 'error');
    } finally {
      setEditSaveLoading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      const updatedUser = await usersService.uploadPhoto(editingUserId, file);
      setUsers(users.map(u => u.id === editingUserId ? updatedUser : u));
      showNotification('Photo uploaded successfully', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to upload photo', 'error');
    }
  };

  const handlePhotoRemove = async () => {
    try {
      const updatedUser = await usersService.removePhoto(editingUserId);
      setUsers(users.map(u => u.id === editingUserId ? updatedUser : u));
      showNotification('Photo removed', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to remove photo', 'error');
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    if (userId === currentUser?.id) {
      showNotification('You cannot change your own status', 'error');
      return;
    }
    try {
      const updatedUser = await usersService.updateProfile(userId, { isActive: !currentStatus });
      setUsers(users.map(u => u.id === userId ? updatedUser : u));
      showNotification(`User status updated to ${!currentStatus ? 'Active' : 'Inactive'}`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to update status', 'error');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!newRole || userId === currentUser?.id) return;
    setRoleChanging(prev => ({ ...prev, [userId]: true }));
    try {
      const data = await usersService.updateUserRole(userId, newRole);
      setUsers(users.map(u => u.id === userId ? data.user : u));
      showNotification(`Role updated to ${ROLE_META[newRole]?.label || newRole}`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to update role', 'error');
    } finally {
      setRoleChanging(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      showNotification('Cannot delete your own account', 'error');
      return;
    }
    const ok = await confirm('Are you sure you want to delete this user? This cannot be undone.', {
      title: 'Delete User',
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await usersService.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      showNotification('User deleted successfully', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to delete user', 'error');
    }
  };

  const handleArchiveUser = async (user) => {
    const ok = await confirm(
      `Archive ${user.firstName}${user.lastName ? ' ' + user.lastName : ''}? They will be hidden from the employee list. This can only be undone from the database.`,
      { title: 'Archive Employee', confirmLabel: 'Archive', danger: true }
    );
    if (!ok) return;
    try {
      await usersService.archiveUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
      showNotification(`${user.firstName} has been archived`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to archive user', 'error');
    }
  };

  const handleToggleArchived = async () => {
    if (!showArchived) {
      try {
        const data = await usersService.getArchivedUsers();
        setArchivedUsers(data.users);
      } catch (error) {
        showNotification(error.message || 'Failed to load archived users', 'error');
        return;
      }
    }
    setShowArchived(v => !v);
  };

  const handleUnarchiveUser = async (user) => {
    try {
      await usersService.unarchiveUser(user.id);
      setArchivedUsers(archivedUsers.filter(u => u.id !== user.id));
      showNotification(`${user.firstName} has been unarchived`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to unarchive user', 'error');
    }
  };

  const handleResetPassword = (user) => {
    setResetModal({ user, mode: 'random', customPwd: '', showPwd: false, loading: false });
  };

  const handleResetSubmit = async () => {
    const { user, mode, customPwd } = resetModal;
    if (mode === 'custom' && customPwd.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }
    setResetModal(m => ({ ...m, loading: true }));
    try {
      const data = await usersService.resetPassword(user.id, mode === 'custom' ? customPwd : undefined);
      setResetModal(m => ({ ...m, loading: false, done: true, password: data.newPassword }));
      showNotification(`Password reset for ${user.firstName}`, 'success');
    } catch (error) {
      setResetModal(m => ({ ...m, loading: false }));
      showNotification(error.message || 'Failed to reset password', 'error');
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/${photo}`;
  };

  const editingUserObj = users.find(u => u.id === editingUserId);

  const thStyle = { padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' };
  const tdStyle = { padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' };

  if (loading) {
    return (
      <div className="main-content flex-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
    <div className="main-content">
        <div className="container">
          <div className="flex-between mb-lg">
            <h1>Employee Management</h1>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              {isSuperAdmin() && (
                <button onClick={handleToggleArchived} className="btn btn-outline">
                  {showArchived ? 'Show Active' : 'Show Archived'}
                </button>
              )}
              {!showArchived && (
                <button onClick={openAddModal} className="btn btn-primary">
                  + Add Employee
                </button>
              )}
            </div>
          </div>

          <div className="mb-md text-secondary">
            {showArchived ? `Archived Users: ${archivedUsers.length}` : `Total Users: ${users.length}`}
          </div>

          <div className="card" style={{ overflow: 'auto' }}>
            <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                  <th style={thStyle}>Photo</th>
                  <th style={thStyle}>Emp No</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Login ID</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(showArchived ? archivedUsers : users).map(user => (
                  <tr key={user.id} className="todo-row" style={{ borderBottom: '1px solid var(--color-gray-200)', cursor: 'pointer' }} onClick={() => setViewingUser(user)}>
                    <td data-label="Photo" style={tdStyle}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          backgroundColor: 'var(--color-gray-200)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {user.photo ? (
                          <img
                            src={getPhotoUrl(user.photo)}
                            alt={user.firstName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>
                            {user.firstName?.charAt(0)?.toUpperCase()}{user.lastName?.charAt(0)?.toUpperCase() || ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Emp No" style={tdStyle}>
                      <span style={{ color: 'var(--color-secondary)' }}>{user.employeeNo || '-'}</span>
                    </td>
                    <td data-label="Name" style={tdStyle}>
                      <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                      </span>
                    </td>
                    <td data-label="Login ID" style={tdStyle}>
                      <span>{user.loginId || '-'}</span>
                    </td>
                    <td data-label="Role" style={tdStyle} onClick={e => e.stopPropagation()}>
                      {user.id === currentUser?.id ? (
                        <RoleBadge role={user.role} employmentType={user.employmentType} />
                      ) : (
                        <RoleDropdown
                          user={user}
                          canAssignSuperAdmin={isSuperAdmin()}
                          changing={!!roleChanging[user.id]}
                          onRoleChange={(role) => handleRoleChange(user.id, role)}
                        />
                      )}
                    </td>
                    <td data-label="Type" style={tdStyle}>
                      <span style={{
                        display: 'inline-block', fontSize: '11px', fontWeight: 600,
                        padding: '2px 8px', borderRadius: '20px',
                        ...(user.employmentType === 'intern'
                          ? { background: 'rgba(217,119,6,0.08)', color: '#b45309', border: '1px solid rgba(217,119,6,0.25)' }
                          : { background: 'rgba(22,163,74,0.08)', color: '#15803d', border: '1px solid rgba(22,163,74,0.25)' })
                      }}>
                        {user.employmentType === 'intern' ? 'Intern' : 'Full-time'}
                      </span>
                    </td>
                    <td data-label="Status" style={tdStyle} onClick={e => e.stopPropagation()}>
                      <span
                        className={`badge ${user.isActive ? 'badge-approved' : 'badge-rejected'}`}
                        onClick={() => handleStatusToggle(user.id, user.isActive)}
                        style={{ cursor: user.id === currentUser?.id ? 'default' : 'pointer', opacity: user.id === currentUser?.id ? 0.6 : 1 }}
                        title={user.id === currentUser?.id ? 'Cannot change your own status' : 'Click to toggle status'}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {showArchived ? (
                          <button
                            onClick={() => handleUnarchiveUser(user)}
                            className="btn btn-secondary btn-sm"
                          >
                            Unarchive
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditClick(user)}
                              className="btn btn-secondary btn-sm"
                            >
                              Edit
                            </button>
                            <ActionsMenu items={[
                              realUser?.role === 'super_admin' && user.id !== realUser.id && {
                                label: 'View as this user',
                                onClick: () => startImpersonation(user),
                              },
                              {
                                label: 'Reset password',
                                onClick: () => handleResetPassword(user),
                              },
                              isSuperAdmin() && !user.isActive && user.id !== currentUser?.id && {
                                label: 'Archive employee',
                                onClick: () => handleArchiveUser(user),
                              },
                              {
                                label: 'Delete employee',
                                onClick: () => handleDeleteUser(user.id),
                                danger: true,
                                disabled: user.id === currentUser?.id,
                              },
                            ]} />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Footer />

      {/* Employee Detail Modal */}
      {viewingUser && (
        <div className="modal-overlay" onClick={() => setViewingUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="flex-between mb-lg">
              <h3>Employee Details</h3>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                {isSuperAdmin() && (
                  <button
                    onClick={() => { setViewingUser(null); handleEditClick(viewingUser); }}
                    className="btn btn-primary btn-sm"
                  >
                    Edit
                  </button>
                )}
                <button onClick={() => setViewingUser(null)} className="btn btn-secondary btn-sm">✕</button>
              </div>
            </div>

            {/* Avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-lg)' }}>
              <div style={{ width: '72px', height: '72px', borderRadius: '2px', overflow: 'hidden', backgroundColor: 'var(--color-gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {viewingUser.photo ? (
                  <img src={getPhotoUrl(viewingUser.photo)} alt={viewingUser.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '1.5rem', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>
                    {viewingUser.firstName?.charAt(0)?.toUpperCase()}{viewingUser.lastName?.charAt(0)?.toUpperCase() || ''}
                  </span>
                )}
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                  {[viewingUser.firstName, viewingUser.lastName].filter(Boolean).join(' ')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: '4px' }}>
                  <RoleBadge role={viewingUser.role} employmentType={viewingUser.employmentType} />
                  <span className={`badge ${viewingUser.isActive ? 'badge-approved' : 'badge-rejected'}`}>
                    {viewingUser.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Details grid */}
            {(() => {
              const labelStyle = { fontSize: 'var(--font-size-xs)', color: 'var(--color-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 'var(--font-weight-semibold)', marginBottom: '2px' };
              const valueStyle = { fontSize: 'var(--font-size-sm)' };
              const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
              const fields = [
                { label: 'Employee No',    value: viewingUser.employeeNo || '—' },
                { label: 'Login ID',       value: viewingUser.loginId || '—' },
                { label: 'Position',       value: viewingUser.position || '—' },
                { label: 'Employment',     value: viewingUser.employmentType === 'intern' ? 'Intern' : 'Permanent' },
                { label: 'Join Date',      value: fmtDate(viewingUser.joinDate) },
                ...(viewingUser.employmentType === 'intern' ? [{ label: 'Internship End', value: fmtDate(viewingUser.internshipEndDate) }] : []),
                { label: 'Office Mobile',   value: viewingUser.officePhone || '—' },
                { label: 'Personal Mobile', value: viewingUser.personalPhone || '—' },
                { label: 'Office Email',   value: viewingUser.officeEmail || '—' },
                { label: 'Personal Email', value: viewingUser.personalEmail || '—' },
              ];
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                  {fields.map(({ label, value }) => (
                    <div key={label}>
                      <div style={labelStyle}>{label}</div>
                      <div style={valueStyle}>{value}</div>
                    </div>
                  ))}
                  {viewingUser.address && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={labelStyle}>Address</div>
                      <div style={{ ...valueStyle, whiteSpace: 'pre-line' }}>{viewingUser.address}</div>
                    </div>
                  )}
                  {isAdmin() && (
                    <div>
                      <div style={labelStyle}>Credit Balance</div>
                      <div style={{ ...valueStyle, fontWeight: 'var(--font-weight-semibold)', color: parseFloat(viewingUser.creditBalance || 0) > 0 ? '#16a34a' : 'inherit' }}>
                        ₹{parseFloat(viewingUser.creditBalance || 0).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex-between mb-lg">
              <h3>Add Employee</h3>
              <button onClick={() => setShowAddModal(false)} className="btn btn-secondary btn-sm">✕</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="grid grid-cols-2 gap-md">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={addData.firstName}
                    onChange={(e) => setAddData({ ...addData, firstName: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={addData.lastName}
                    onChange={(e) => setAddData({ ...addData, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-md">
                <div className="form-group">
                  <label className="form-label">Preferred Short Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={addData.shortName}
                    onChange={(e) => setAddData({ ...addData, shortName: e.target.value })}
                    placeholder=""
                    required
                  />
                  {addData.shortName.trim() && (
                    <small className="text-secondary">
                      Login ID: <strong>{addData.shortName.trim().toLowerCase().replace(/\s+/g, '')}</strong>
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Employee No</label>
                  <input
                    type="text"
                    className="form-input"
                    value={addData.employeeNo}
                    onChange={(e) => setAddData({ ...addData, employeeNo: e.target.value.replace(/[^0-9]/g, '') })}
                    inputMode="numeric"
                    placeholder=""
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-md">
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <Select
                    value={addData.role}
                    onChange={v => setAddData({ ...addData, role: v })}
                    options={[
                      { value: 'employee', label: 'User' },
                      { value: 'admin', label: 'Admin' },
                      ...(isSuperAdmin() ? [{ value: 'super_admin', label: 'Super Admin' }] : []),
                    ]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type *</label>
                  <Select
                    value={addData.employmentType}
                    onChange={v => setAddData({ ...addData, employmentType: v, internDuration: '3' })}
                    options={[
                      { value: 'permanent', label: 'Permanent' },
                      { value: 'intern', label: 'Intern' },
                    ]}
                  />
                </div>
              </div>
              {addData.employmentType === 'intern' && (
                <div className="form-group">
                  <label className="form-label">Internship Duration *</label>
                  <Select
                    value={addData.internDuration}
                    onChange={v => setAddData({ ...addData, internDuration: v })}
                    options={[
                      { value: '3',  label: '3 months' },
                      { value: '6',  label: '6 months' },
                      { value: '12', label: '1 year' },
                    ]}
                  />
                  <small className="text-secondary" style={{ marginTop: '4px', display: 'block' }}>
                    End date: {addMonths(new Date().toISOString().split('T')[0], parseInt(addData.internDuration)).split('-').reverse().join('/')}
                  </small>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Initial Password *</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showAddPassword ? 'text' : 'password'}
                    className="form-input"
                    value={addData.password}
                    onChange={(e) => setAddData({ ...addData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Min. 6 characters"
                    style={{ paddingRight: '80px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAddPassword(v => !v)}
                    style={{ position: 'absolute', right: 'var(--spacing-sm)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-secondary)', fontSize: 'var(--font-size-sm)', padding: 0 }}
                  >
                    {showAddPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <small className="text-secondary">Share this with the employee — they can change it later</small>
              </div>
              <div className="flex gap-md">
                <button type="submit" className="btn btn-primary" disabled={addLoading}>
                  {addLoading ? (
                    <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Adding...</span>
                  ) : 'Add Employee'}
                </button>
                <button type="button" onClick={() => setShowAddModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && editingUserObj && (
        <div className="modal-overlay" onClick={handleCloseEdit}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="flex-between mb-lg">
              <h3>Edit Employee</h3>
              <button onClick={handleCloseEdit} className="btn btn-secondary btn-sm">✕</button>
            </div>

            {/* Photo */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 'var(--spacing-lg)', gap: 'var(--spacing-sm)' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  backgroundColor: 'var(--color-gray-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed var(--color-primary)',
                  position: 'relative'
                }}
                onClick={() => photoInputRef.current?.click()}
                title="Click to upload photo"
              >
                {editingUserObj.photo ? (
                  <img
                    src={getPhotoUrl(editingUserObj.photo)}
                    alt={editingUserObj.firstName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ fontSize: '1.5rem', color: 'var(--color-secondary)' }}>
                    {editingUserObj.firstName?.charAt(0)?.toUpperCase()}{editingUserObj.lastName?.charAt(0)?.toUpperCase() || ''}
                  </span>
                )}
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files[0]) handlePhotoUpload(e.target.files[0]);
                }}
              />
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>
                  Click photo to change
                </span>
                {editingUserObj.photo && (
                  <button
                    type="button"
                    onClick={handlePhotoRemove}
                    className="btn btn-error btn-sm"
                    style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={handleSaveEdit}>
              {/* Personal Information */}
              <div style={{ marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-xs)', borderBottom: '1px solid var(--color-gray-200)' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-md)' }}>
                  Personal Information
                </div>
                <div className="grid grid-cols-2 gap-md">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editData.firstName}
                      onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editData.lastName}
                      onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-md">
                  <div className="form-group">
                    <label className="form-label">Personal Mobile</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder=""
                      value={editData.personalPhone}
                      onChange={(e) => setEditData({ ...editData, personalPhone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Personal Email</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder=""
                      value={editData.personalEmail}
                      onChange={(e) => setEditData({ ...editData, personalEmail: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-input"
                    placeholder=""
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    rows={2}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Official Information */}
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-md)' }}>
                  Official Information
                </div>
                <div className="grid grid-cols-2 gap-md">
                  <div className="form-group">
                    <label className="form-label">Employee No *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editData.employeeNo}
                      onChange={(e) => setEditData({ ...editData, employeeNo: e.target.value.replace(/[^0-9]/g, '') })}
                      inputMode="numeric"
                      placeholder=""
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Preferred Short Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editData.shortName}
                      onChange={(e) => setEditData({ ...editData, shortName: e.target.value })}
                      placeholder=""
                      required
                    />
                    {editData.shortName.trim() && (
                      <small className="text-secondary">
                        Login ID: <strong>{editData.shortName.trim().toLowerCase().replace(/\s+/g, '')}</strong>
                      </small>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-md">
                  <div className="form-group">
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder=""
                      value={editData.position}
                      onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Join Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={editData.joinDate}
                      onChange={(e) => setEditData({ ...editData, joinDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type</label>
                  <Select
                    value={editData.employmentType}
                    onChange={v => setEditData({ ...editData, employmentType: v, internshipEndDate: v === 'permanent' ? '' : (editData.internshipEndDate || (editData.joinDate ? addMonths(editData.joinDate, 3) : '')) })}
                    options={[
                      { value: 'permanent', label: 'Permanent' },
                      { value: 'intern', label: 'Intern' },
                    ]}
                  />
                </div>
                {editData.employmentType === 'intern' && (
                  <div className="form-group">
                    <label className="form-label">Internship Duration</label>
                    <Select
                      value={(() => {
                        if (!editData.joinDate || !editData.internshipEndDate) return '3';
                        const m6  = addMonths(editData.joinDate, 6);
                        const m12 = addMonths(editData.joinDate, 12);
                        if (editData.internshipEndDate === m12) return '12';
                        if (editData.internshipEndDate === m6)  return '6';
                        return '3';
                      })()}
                      onChange={v => setEditData({ ...editData, internshipEndDate: editData.joinDate ? addMonths(editData.joinDate, parseInt(v)) : editData.internshipEndDate })}
                      options={[
                        { value: '3',  label: '3 months' },
                        { value: '6',  label: '6 months' },
                        { value: '12', label: '1 year' },
                      ]}
                    />
                    {editData.joinDate && (
                      <small className="text-secondary" style={{ marginTop: '4px', display: 'block' }}>
                        End date: {editData.internshipEndDate
                          ? editData.internshipEndDate.split('-').reverse().join('/')
                          : addMonths(editData.joinDate, 3).split('-').reverse().join('/')}
                      </small>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-md">
                  <div className="form-group">
                    <label className="form-label">Office Mobile</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder=""
                      value={editData.officePhone}
                      onChange={(e) => setEditData({ ...editData, officePhone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Office Email</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder=""
                      value={editData.officeEmail}
                      onChange={(e) => setEditData({ ...editData, officeEmail: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </form>

            {/* Documents */}
            <div style={{ marginTop: 'var(--spacing-xl)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--spacing-lg)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-md)' }}>
                Documents
              </div>

              {/* Hidden file inputs */}
              <input ref={aadharInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files[0]) handleDocUpload('aadhar', [e.target.files[0]]); e.target.value = ''; }} />
              <input ref={offerLetterInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files[0]) handleDocUpload('offer_letter', [e.target.files[0]]); e.target.value = ''; }} />
              <input ref={certInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files.length > 0) handleDocUpload('certificate', Array.from(e.target.files)); e.target.value = ''; }} />

              {empDocsLoading ? (
                <div className="flex-center gap-sm" style={{ padding: 'var(--spacing-md) 0', color: 'var(--color-text-secondary)' }}>
                  <div className="spinner spinner-sm"></div> Loading documents...
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {/* Aadhaar Card */}
                  {(() => {
                    const doc = empDocs.find(d => d.type === 'aadhar');
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)' }}>
                        <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>Aadhaar Card</span>
                        {doc ? (
                          <>
                            <a href={`${baseUrl}/uploads/${doc.filename}`} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={doc.originalName}>{doc.originalName}</a>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => aadharInputRef.current?.click()}>Replace</button>
                            <button type="button" className="btn btn-error btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => handleDocDelete(doc.id)}>Delete</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>Not uploaded</span>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => aadharInputRef.current?.click()}>Upload</button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Offer Letter */}
                  {(() => {
                    const doc = empDocs.find(d => d.type === 'offer_letter');
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)' }}>
                        <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>Offer Letter</span>
                        {doc ? (
                          <>
                            <a href={`${baseUrl}/uploads/${doc.filename}`} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={doc.originalName}>{doc.originalName}</a>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => offerLetterInputRef.current?.click()}>Replace</button>
                            <button type="button" className="btn btn-error btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => handleDocDelete(doc.id)}>Delete</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>Not uploaded</span>
                            <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => offerLetterInputRef.current?.click()}>Upload</button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Educational Certificates */}
                  {(() => {
                    const certs = empDocs.filter(d => d.type === 'certificate');
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                    return (
                      <div style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: certs.length > 0 ? 'var(--spacing-sm)' : 0 }}>
                          <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>
                            Educational Certificates {certs.length > 0 && <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>({certs.length})</span>}
                          </span>
                          <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                            onClick={() => certInputRef.current?.click()}>Add</button>
                        </div>
                        {certs.length === 0 && (
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>No certificates uploaded</span>
                        )}
                        {certs.map(cert => (
                          <div key={cert.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-xs)' }}>
                            <a href={`${baseUrl}/uploads/${cert.filename}`} target="_blank" rel="noopener noreferrer"
                              style={{ flex: 1, fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={cert.originalName}>{cert.originalName}</a>
                            <button type="button" className="btn btn-error btn-sm" style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => handleDocDelete(cert.id)}>Delete</button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Credit Balance — admin and super_admin */}
            {isAdmin() && (
              <div style={{ marginTop: 'var(--spacing-xl)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--spacing-lg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-md)' }}>
                  <div className="form-label" style={{ margin: 0 }}>Credit Balance</div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: editUserBalance > 0 ? '#16a34a' : editUserBalance < 0 ? '#DC2626' : 'inherit' }}>
                    ₹{editUserBalance.toFixed(2)}
                  </div>
                </div>

                <form onSubmit={handleAddCredit} style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap', marginBottom: 'var(--spacing-md)' }}>
                  <input
                    type="number"
                    className="form-input"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    step="0.01"
                    min="0.01"
                    placeholder="Amount"
                    style={{ width: '110px', flexShrink: 0 }}
                    required
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={creditNote}
                    onChange={(e) => setCreditNote(e.target.value)}
                    placeholder="Note (optional)"
                    style={{ flex: 1, minWidth: '120px' }}
                  />
                  <button type="submit" className="btn btn-primary btn-sm" disabled={creditAdding} style={{ flexShrink: 0 }}>
                    {creditAdding ? <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Adding...</span> : 'Add Credit'}
                  </button>
                </form>

                {creditTxns.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {creditTxns.slice(0, 5).map(tx => (
                      <div key={tx.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '4px 0', borderBottom: '1px solid var(--color-gray-100)',
                        fontSize: 'var(--font-size-xs)', color: 'var(--color-secondary)'
                      }}>
                        <span>{new Date(tx.createdAt).toLocaleDateString('en-GB')} — {tx.note || (tx.type === 'credit' ? 'Credit added' : 'Deduction')}</span>
                        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: tx.type === 'credit' ? '#16a34a' : '#DC2626', marginLeft: 'var(--spacing-sm)' }}>
                          {tx.type === 'credit' ? '+' : '-'}₹{parseFloat(tx.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {creditTxns.length === 0 && (
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-secondary)', fontStyle: 'italic' }}>No transactions yet.</p>
                )}
              </div>
            )}

            <div className="flex gap-md" style={{ marginTop: 'var(--spacing-xl)', paddingTop: 'var(--spacing-lg)', borderTop: '1px solid var(--color-gray-200)' }}>
              <button type="button" onClick={handleSaveEdit} className="btn btn-primary" disabled={editSaveLoading}>
                {editSaveLoading ? (
                  <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Saving...</span>
                ) : 'Save Changes'}
              </button>
              <button type="button" onClick={handleCloseEdit} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal" style={{ maxWidth: '420px', width: '90vw' }}>
            <div className="flex-between mb-lg">
              <h3>Reset Password</h3>
              <button onClick={() => setResetModal(null)} className="btn btn-secondary btn-sm">✕</button>
            </div>

            {!resetModal.done ? (
              <form onSubmit={e => { e.preventDefault(); handleResetSubmit(); }}>
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-lg)' }}>
                  Reset password for <strong>{resetModal.user.firstName}</strong> ({resetModal.user.loginId})
                </p>

                {/* Mode selector */}
                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <div
                    onClick={() => setResetModal(m => ({ ...m, mode: 'random', customPwd: '' }))}
                    style={{
                      cursor: 'pointer', padding: '10px 14px', borderRadius: '2px', marginBottom: '8px',
                      border: `1px solid ${resetModal.mode === 'random' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                      background: resetModal.mode === 'random' ? 'rgba(var(--color-primary-rgb,183,28,28),0.04)' : 'var(--color-gray-50)',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                      <input type="radio" checked={resetModal.mode === 'random'} onChange={() => setResetModal(m => ({ ...m, mode: 'random', customPwd: '' }))} onClick={e => e.stopPropagation()} style={{ accentColor: 'var(--color-primary)', cursor: 'pointer', width: '16px', height: '16px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>Generate random password</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>A secure 8-character password will be created</div>
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => setResetModal(m => ({ ...m, mode: 'custom' }))}
                    style={{
                      cursor: 'pointer', padding: '10px 14px', borderRadius: '2px',
                      border: `1px solid ${resetModal.mode === 'custom' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                      background: resetModal.mode === 'custom' ? 'rgba(var(--color-primary-rgb,183,28,28),0.04)' : 'var(--color-gray-50)',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                      <input type="radio" checked={resetModal.mode === 'custom'} onChange={() => setResetModal(m => ({ ...m, mode: 'custom' }))} onClick={e => e.stopPropagation()} style={{ accentColor: 'var(--color-primary)', cursor: 'pointer', width: '16px', height: '16px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>Set custom password</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>Enter a specific password manually</div>
                      </div>
                    </div>
                  </div>
                </div>

                {resetModal.mode === 'custom' && (
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input
                        type={resetModal.showPwd ? 'text' : 'password'}
                        className="form-input"
                        value={resetModal.customPwd}
                        onChange={e => setResetModal(m => ({ ...m, customPwd: e.target.value }))}
                        placeholder="Min. 6 characters"
                        minLength={6}
                        autoFocus
                        style={{ paddingRight: '60px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setResetModal(m => ({ ...m, showPwd: !m.showPwd }))}
                        style={{ position: 'absolute', right: 'var(--spacing-sm)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-secondary)', fontSize: 'var(--font-size-sm)', padding: 0 }}
                      >
                        {resetModal.showPwd ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-md">
                  <button type="submit" className="btn btn-primary" disabled={resetModal.loading}>
                    {resetModal.loading ? <span className="flex-center gap-sm"><div className="spinner spinner-sm"></div>Resetting...</span> : 'Reset Password'}
                  </button>
                  <button type="button" onClick={() => setResetModal(null)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            ) : (
              <>
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-md)' }}>
                  New password for <strong>{resetModal.user.firstName}</strong> ({resetModal.user.loginId}). Copy and share with the employee:
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)', marginBottom: 'var(--spacing-lg)', fontFamily: 'monospace', fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)' }}>
                  <span style={{ flex: 1, userSelect: 'all' }}>{resetModal.password}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(resetModal.password)}
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '11px', flexShrink: 0 }}
                  >
                    Copy
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setResetModal(null)} className="btn btn-primary">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Users;
